import React from 'react';

/**
 * A white panel with an optional header row (title, subtitle and actions on the right).
 *
 * @param {Object} props - Card properties
 * @param {string} props.title - Card title
 * @param {string} props.subtitle - One line under the title
 * @param {React.ReactNode} props.headerAction - Buttons or filters at the right of the header
 * @param {boolean} props.hoverable - Lifts the border on hover (for clickable cards)
 * @param {boolean} props.noPadding - Body without padding (tables that run edge to edge)
 * @param {string} props.bodyClassName - Extra classes for the body
 */
const Card = ({
  children,
  title,
  subtitle,
  headerAction,
  variant,
  hoverable = false,
  noPadding = false,
  className = '',
  bodyClassName = '',
  onClick,
  ...props
}) => {
  const classes = [
    'rounded-xl border border-ui-line bg-ui-card shadow-card',
    hoverable || onClick ? 'transition-colors hover:border-neutral-300 dark:hover:border-neutral-600' : '',
    onClick ? 'cursor-pointer' : '',
    className,
  ].filter(Boolean).join(' ');

  const hasHeader = title || subtitle || headerAction;

  return (
    <section className={classes} onClick={onClick} {...props}>
      {hasHeader && (
        <header className="flex min-h-12 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-ui-line-soft px-5 py-3">
          <div className="min-w-0 shrink-0">
            {title && <h2 className="text-[15px] font-semibold text-ui-ink">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-[13px] text-ui-ink-3">{subtitle}</p>}
          </div>
          {headerAction && <div className="flex min-w-0 flex-wrap items-center gap-2">{headerAction}</div>}
        </header>
      )}
      <div className={`${noPadding ? '' : 'p-5'} ${bodyClassName}`}>{children}</div>
    </section>
  );
};

export default Card;
